import { TitleButton } from '@/common/buttons/TitleButton';
import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { XAxisButton } from '@/common/dashboard/XAxisButton';
import { useForm } from '@/common/form/FormContext';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo, useState } from 'react';
import { McdInvTrendDetailGrid } from './McdInvTrendDetailGrid';

// The kit has no column-chart icon, so the bar chart is turned upright
const viewOptions = [
  { value: 'bar', icon: far.faChartBar, transform: { rotate: 270, flipX: true }, toolTip: 'Bar Chart' },
  { value: 'line', icon: far.faChartLine, toolTip: 'Line Chart' },
];

const measureOptions = [
  { value: 'AMT', label: '$', toolTip: 'Amounts', yLabel: 'Amount in $' },
  { value: 'UNITS', label: 'Units', toolTip: 'Units', yLabel: 'Units' },
];

export const McdInvTrendCard = ({ xAxisOptions, metricOptions }) => {
  const { formValues, assignValue } = useForm();
  const [view, setView] = useState('bar');
  const [drillCategory, setDrillCategory] = useState(null);

  // A view saved before an axis was removed (e.g. Contract Type) falls back to the first axis
  const xAxisOption = xAxisOptions.find((option) => option.code === formValues.xAxis) ?? xAxisOptions[0];
  const xAxis = xAxisOption.code;

  // Metric and measure are applied client-side since the plugin returns every series
  const reqParam = useMemo(
    () => ({
      prodExpr: formValues.prodExpr,
      brandExpr: formValues.brandExpr,
      vendorExpr: formValues.vendorExpr,
      ctrtExpr: formValues.ctrtExpr,
      rbtPeriodExpr: formValues.rbtPeriodExpr,
      earnedPeriodExpr: formValues.earnedPeriodExpr,
      stateExpr: formValues.stateExpr,
      xAxis,
    }),
    [
      formValues.prodExpr,
      formValues.brandExpr,
      formValues.vendorExpr,
      formValues.ctrtExpr,
      formValues.rbtPeriodExpr,
      formValues.earnedPeriodExpr,
      formValues.stateExpr,
      xAxis,
      // Not sent to the backend, but forces a refetch when switching the card between chart and grid plugins
      drillCategory,
    ],
  );

  const drillIn = (point) => {
    // Unmatched rows have no single value to drill into
    if (point.category === '(None)') return;
    setDrillCategory(point.category);
  };

  const measureOption = measureOptions.find((option) => option.value === formValues.measure) ?? measureOptions[0];
  const metric =
    metricOptions
      .filter((option) => formValues.metric?.includes(option.value))
      .map((option) => option.displayName)
      .join(' & ') || metricOptions[0].displayName;
  const axisLabel = xAxisOption.axisLabel;

  return (
    <DashboardCard
      title={drillCategory ? 'Invoice Lines' : undefined}
      description={'Displays the Medicaid Invoice ' + metric + ' ' + measureOption.toolTip + ' by ' + axisLabel}
      module="sc"
      plugin={drillCategory ? 'MCD_INV_TREND_DETAIL_DATA_PLUGIN' : 'MCD_INV_TREND_DATA_PLUGIN'}
      reqParam={reqParam}
      buttons={
        drillCategory ?
          <TitleButton icon={far.faArrowLeft} toolTip="Back to Chart" onClick={() => setDrillCategory(null)} />
        : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <XAxisButton options={xAxisOptions} />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={measureOption.value}
              onChange={(event, value) => value && assignValue('measure', value)}
            >
              {measureOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  title={option.toolTip}
                  sx={{ textTransform: 'none', minWidth: 36 }}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup exclusive size="small" value={view} onChange={(event, value) => value && setView(value)}>
              {viewOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value} title={option.toolTip}>
                  {/* The span gives the icon the text's line height, so these match the $/Units buttons */}
                  <span>
                    <FontAwesomeIcon icon={option.icon} transform={option.transform} />
                  </span>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
      }
    >
      {(data) =>
        drillCategory ?
          <McdInvTrendDetailGrid rows={data} xAxis={xAxis} category={drillCategory} />
        : <CartesianChart
            data={{
              ...data,
              series: (
                data.series?.filter(
                  (s) => s.measure === measureOption.value && formValues.metric?.includes(s.metric),
                ) ?? []
              ).map((s) => ({
                ...s,
                type: view === 'line' ? 'line' : 'column',
              })),
            }}
            onClick={(point) => drillIn(point)}
            legend="top"
            xLabel={axisLabel}
            yLabel={measureOption.yLabel}
            markers={view === 'line'}
            allowNegative
            rotateXLabels
          />
      }
    </DashboardCard>
  );
};
